import { useState, useRef, useEffect } from "react";
import type { UIMessage } from "ai";
import { useNotification } from "@/hooks/useNotification";
import { deleteMessage, upsertMessage } from "instantdb/client/message";
import { useParams } from "@tanstack/react-router";

interface AIEditMessageProps {
  message: UIMessage & { metadata: { model: string } };
  messages: UIMessage[];
  currentModel?: string;
  onRetry?: (content: string, model: string) => void;
  onCancel: () => void;
}

export function AIEditMessage({
  message,
  messages,
  currentModel,
  onRetry,
  onCancel,
}: AIEditMessageProps) {
  const [editedContent, setEditedContent] = useState("");
  const { showNotification } = useNotification();
  const params = useParams({ strict: false });
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Get the model used for this specific message from metadata
  const messageModel = message.metadata?.model || currentModel;

  // Auto-resize textarea
  const autoResize = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    textarea.style.height = textarea.scrollHeight + "px";
  };

  // Initialize content and focus when component mounts
  useEffect(() => {
    const content =
      message.parts
        ?.filter((part: any) => part.type === "text")
        .map((part: any) => part.text)
        .join("\n") || "";

    setEditedContent(content);

    if (textareaRef.current) {
      textareaRef.current.focus();
      // Set cursor to the end
      const length = content.length;
      textareaRef.current.setSelectionRange(length, length);
      autoResize(textareaRef.current);
    }
  }, [message]);

  // Global escape key handler
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [onCancel]);

  const handleSaveEdit = async () => {
    try {
      if (!params.chatId) {
        showNotification({
          type: "error",
          message: "Chat ID not found",
        });
        return;
      }

      const trimmedContent = editedContent.trim();
      if (!trimmedContent) {
        showNotification({
          type: "error",
          message: "Message cannot be empty",
        });
        return;
      }

      // Update the message content in database
      await upsertMessage(
        {
          id: message.id,
          content: trimmedContent,
          updatedAt: new Date().toISOString(),
        },
        params.chatId,
      );

      // Find and delete the AI response if it exists
      const currentIndex = messages.findIndex((m) => m.id === message.id);
      if (currentIndex + 1 < messages.length && messages[currentIndex + 1].role === "assistant") {
        const aiResponseId = messages[currentIndex + 1].id;
        await deleteMessage(aiResponseId);
      }

      // Always regenerate AI response with edited content
      // Use the model from the message metadata (which preserves whether it's an image model)
      onRetry?.(trimmedContent, messageModel || currentModel || "");

      onCancel(); // Exit edit mode
    } catch (error) {
      console.error("Error saving edit:", error);
      showNotification({
        type: "error",
        message: "Failed to save changes",
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      onCancel();
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedContent(e.target.value);
    autoResize(e.target);
  };

  return (
    <div className="w-full">
      <textarea
        ref={textareaRef}
        value={editedContent}
        onChange={handleTextareaChange}
        onKeyDown={handleKeyDown}
        className="w-full min-w-[250px] bg-background border border-input rounded-md px-3 py-2 text-sm resize-none ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/20 focus-visible:ring-offset-1"
        placeholder="Edit your message..."
      />
    </div>
  );
}
